import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DownloadOutlined, FileSearchOutlined } from '@ant-design/icons';
import { Tooltip, Button, message, Input, Select } from 'antd';
import {
  dateMonthFormat,
  formatBRNumber,
  formatCurrency,
  getDateInISOFormat,
} from '@utils/formatters';
import moment from 'moment';
import useSwr from '@hooks/useSwr';
import api from '@services/api';
import Table from '@components/Table';
import QuickRangePicker from '@components/DatePicker/QuickRangePicker';
import useSearchParams from '@hooks/useSearchParams';
import { useNavigate } from 'react-router-dom';
import CustomTag from '@components/CustomTag';
import { IContract } from '@components/DeviceActionModal/types';

const { Search } = Input;

const DEFAULT_DATES = {
  // 3 MONTHS AGO
  startDate: getDateInISOFormat(moment().subtract(3, 'months').toISOString()),
  // TODAY
  endDate: getDateInISOFormat(moment().toISOString()),
};

const Invoices = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['invoices', 'reports', 'attributes']);

  const [requestingReport, setRequestingReport] = useState(false);
  const [numberValue, setNumberValue] = useState('');

  const { data: contracts } = useSwr<IContract[] | ''>(
    '/service-proxy/contracts',
  );

  const contractsOptions = useMemo(
    () =>
      Array.isArray(contracts)
        ? contracts?.map(({ assetNumber, planName, child }) => {
            const labelParts = [assetNumber, planName].filter(Boolean);
            const label = labelParts.join(' - ');

            if (child?.length > 0) {
              return {
                label,
                options: child?.map(childItem => {
                  const childLabelParts = [
                    childItem?.assetNumber,
                    childItem?.acronym,
                    childItem?.planName,
                  ].filter(Boolean);
                  const childLabel = childLabelParts.join(' - ');
                  return {
                    label: childLabel,
                    value: childItem?.assetNumber?.replace('A', ''),
                  };
                }),
              };
            }

            return {
              label,
              value: assetNumber?.replace('A', ''),
            };
          })
        : undefined,
    [contracts],
  );

  const {
    setSearchParams,
    hasValuesInExtraParams,
    clearSearchParams,
    searchParams,
  } = useSearchParams({
    type: 'pagination',
    defaultParams: ['startDate', 'endDate'],
    extraParams: ['number', 'status', 'customerId', 'contractId'],
  });

  const {
    startDate,
    endDate,
    number,
    status,
    customerId,
    contractId,
    page,
    linesPerPage,
  } = useMemo(() => searchParams, [searchParams]);

  const params = useMemo(
    () => ({
      startDate: startDate ?? DEFAULT_DATES.startDate,
      endDate: endDate ?? DEFAULT_DATES.endDate,
      status,
      customerId,
      nuAssetId: contractId,
    }),
    [startDate, endDate, status, customerId, contractId],
  );

  const { data } = useSwr('/service-proxy/invoices', {
    params,
  });

  const invoicesWithPlans = useMemo(() => {
    if (!data) return [];
    const invoices = data?.listaFaturas;
    const plans = data?.dadosPlano;
    return invoices?.map((base: any) => {
      const dsPlans = plans
        ?.filter((item: any) => item.nuAssetId === base.nuAssetId)
        ?.map((plan: any) => plan?.dsPlano);
      return {
        ...base,
        dsPlans,
      };
    });
  }, [data]);

  const dataFiltered = useMemo(
    () =>
      invoicesWithPlans?.filter((invoice: any) =>
        number ? invoice.nuFaturaKenan === number.trim() : invoice,
      ),
    [invoicesWithPlans, number],
  );

  useEffect(() => {
    setNumberValue(number);
  }, [number]);

  const contractDescriptionLink = useCallback(
    (value: any) => (
      <Tooltip title={t('view-contract')}>
        <Button
          type="link"
          onClick={() => {
            navigate(`/financial/contracts?contractId=A${value}`);
          }}
        >
          {`A${value}`}
        </Button>
      </Tooltip>
    ),
    [t, navigate],
  );

  const columns = useMemo(
    () => [
      {
        title: t('table.nuFaturaKenan'),
        dataIndex: 'id_fatura',
        stringFiltered: true,
      },
      {
        title: t('table.medicao'),
        dataIndex: 'medicao',
        render: (
          value: string,
          record: {
            dataInicio: string;
            dataTermino: string;
          },
        ) =>
          `${record?.dataInicio.replaceAll('-', '/') ?? '-'} ${t(
            'detailed.invoice.to',
          )} ${record?.dataTermino.replaceAll('-', '/') ?? '-'}`,
      },
      {
        title: t('table.dataVencimento'),
        dataIndex: 'dataVencimento',
        align: 'center' as const,
        render: (value: string) => dateMonthFormat(value, t),
      },
      {
        title: t('table.nuChips'),
        dataIndex: 'nuChips',
        align: 'center' as const,
        render: (
          value: string,
          record: {
            nuChipsCobrados: string;
            nuChipsMulta: string;
          },
        ) =>
          `${formatBRNumber(record?.nuChipsCobrados)} / ${formatBRNumber(
            record?.nuChipsMulta,
          )}`,
      },
      {
        title: t('table.nuValorFatura'),
        dataIndex: 'nuValorFatura',
        render: (value: number) => formatCurrency(value),
      },
      {
        title: t('searchResultItem.status'),
        dataIndex: 'status',
        align: 'center' as const,
        render: (value: string) => (
          <CustomTag color={value === 'opened' ? 'green' : 'red'}>
            {value === 'opened'
              ? t('searchResultItem.opened')
              : t('searchResultItem.closed')}
          </CustomTag>
        ),
      },
      {
        title: t('table.nuAssetId'),
        dataIndex: 'nuAssetId',
        align: 'center' as const,
        render: contractDescriptionLink,
      },
      {
        title: t('attributes:plan'),
        dataIndex: 'dsPlans',
        align: 'center' as const,
        render: (dsPlans: string[]) => dsPlans?.join(';'),
      },
      {
        title: t('attributes:actions'),
        dataIndex: 'actions',
        align: 'center' as const,
        render: (_: any, record: any) => (
          <Button
            icon={<FileSearchOutlined />}
            onClick={() => {
              navigate(`${record.status}/${record.id_fatura}`);
            }}
          />
        ),
      },
    ],
    [t, navigate, contractDescriptionLink],
  );

  const navigateToReports = useCallback(() => {
    message.destroy('report-requested');
    navigate('/support/reports?type=FATURAS');
  }, [navigate]);

  const handleExportInvoices = useCallback(() => {
    setRequestingReport(true);
    api
      .post('/report-request/', { relatoryType: 'FATURAS' })
      .then(() => {
        message.success({
          content: (
            <span>
              {t('reports:messages.request-report-success')}
              {t('reports:messages.check-report-progress-and-download')}
              <Button
                type="link"
                onClick={navigateToReports}
                style={{ padding: 3 }}
              >
                {t('reports:messages.go-to-reports')}
              </Button>
            </span>
          ),
          key: 'report-requested',
          duration: 3,
        });
      })
      .catch(() => message.error('reports:messages.request-report-error'))
      .finally(() => setRequestingReport(false));
  }, [navigateToReports, t]);

  const extraActions = useMemo(
    () => (
      <Tooltip title={t('reports:messages.request-report')}>
        <Button
          icon={<DownloadOutlined />}
          disabled={requestingReport}
          loading={requestingReport}
          onClick={handleExportInvoices}
        />
      </Tooltip>
    ),
    [handleExportInvoices, requestingReport, t],
  );

  const handleDateChange = useCallback(
    (values: any) => {
      const start = getDateInISOFormat(values.startDate);
      const end = getDateInISOFormat(values.endDate);
      setSearchParams({ startDate: start, endDate: end });
    },
    [setSearchParams],
  );

  const statusOptions = useMemo(
    () => [
      { label: t('searchResultItem.opened'), value: 'opened' },
      { label: t('searchResultItem.closed'), value: 'closed' },
    ],
    [t],
  );

  const tableFilters = useMemo(
    () => [
      {
        visible: true,
        col: {
          lg: 8,
          xl: 8,
          xs: 24,
        },
        label: t('attributes:date'),
        element: (
          <QuickRangePicker
            onChange={handleDateChange}
            value={
              startDate
                ? [moment(startDate, 'YYYYMMDD'), moment(endDate, 'YYYYMMDD')]
                : undefined
            }
            defaultValue={
              startDate
                ? [moment(startDate, 'YYYYMMDD'), moment(endDate, 'YYYYMMDD')]
                : [moment().subtract(3, 'months'), moment()]
            }
            style={{ width: '100%' }}
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 8,
          xl: 8,
          xs: 24,
        },
        label: t('table.nuFaturaKenan'),
        element: (
          <Search
            onSearch={value => setSearchParams({ number: value })}
            value={numberValue}
            onChange={({ target: { value } }) => setNumberValue(value)}
            allowClear
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 8,
          xl: 8,
          xs: 24,
        },
        label: t('search.statusPlaceholder'),
        element: (
          <Select
            options={statusOptions}
            style={{ width: '100%' }}
            allowClear
            value={status}
            onChange={value => {
              setSearchParams({
                status: value,
              });
              if (!value || value === 'O') {
                setSearchParams({
                  status: value,
                  initialDate: undefined,
                  endDate: undefined,
                });
              }
            }}
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 16,
          xl: 16,
          xs: 24,
        },
        label: t('table.nuAssetId'),
        element: (
          <Select
            options={contractsOptions}
            value={contractId}
            onChange={value => setSearchParams({ contractId: value })}
            style={{ width: '100%' }}
            showSearch
            allowClear
          />
        ),
      },
    ],
    [
      t,
      statusOptions,
      status,
      numberValue,
      contractsOptions,
      contractId,
      handleDateChange,
      startDate,
      endDate,
      setSearchParams,
    ],
  );

  return (
    <Table
      columns={columns}
      data={dataFiltered}
      title={t('title')}
      tableKey="invoicesMultiConnect"
      extraActions={extraActions}
      filters={{
        openByDefault: true,
        list: tableFilters,
        hasValuesInFilters: hasValuesInExtraParams,
        onClearFilters: () => clearSearchParams(),
      }}
      pagination={{
        page,
        linesPerPage,
        total: dataFiltered?.length,
      }}
      onTableChange={setSearchParams}
      disabledPaginationWhenAll={false}
    />
  );
};

export default Invoices;
